import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import React, { useEffect, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { CREATE_TYPE } from "@/store/projectsSlice";
import { track } from "@/utils/events/tracker";
import {
  ProjectRunMode,
  ReactFramework,
  UiLibrary,
} from "@/utils/templateProjects";
import {
  getAxiosErorrMessage,
  getUnexpiredToken,
  logAxiosError,
  useCanvasId,
  useUserId,
} from "@/utils/utils";
import { DialogClose } from "@radix-ui/react-dialog";
import { CaretSortIcon } from "@radix-ui/react-icons";
import axios from "axios";
import { CheckIcon, Loader2, PlusIcon, Trash2Icon } from "lucide-react";
import { useSelector } from "react-redux";
import { NavLink } from "react-router";
import { toast } from "sonner";
import InputPrefixWrapper from "./Core/InputPrefixWrapper";
import EnvVariableHandler from "./EnvVariableHandler";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { Input } from "./ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Switch } from "./ui/switch";

import { useUser, useSession } from "@clerk/clerk-react";
import { GitProvider } from "@/store/gitStatusSlice";
const SECRET_ENV_VARIABLE_VALUE = "SECRET_ENV_VARIABLE_VALUE";
const DELETE_ENV_VARIABLE_CONSTANT = "DELETE_ENV_VARIABLE_CONSTANT";

interface Props {
  editProjectId?: string;
  editProjectData?: {
    name: string;
    repoPath: string;
    framework: string;
    mainFilePath: string;
    projectSubfolderPath: string;
    srcFolderPath: string;
    customInstallCommand: string;
    devServerPort: string;
    customRunCommand: string;
    customInstallExtraCommand: string;
    nodeVersion: string;
    packageManager: string;
    exampleComponentPath: string;
    disableAutoformat?: boolean;
    auto_install_off: boolean;
    base_branch?: string;
  };
  close: (project?: any, canvas?: any) => void;
  provider?: GitProvider;
}

const AddEditProjectModal: React.FC<Props> = (props) => {
  const canvasId = useCanvasId();

  const createType = useSelector((state: any) => state.projects.createType);

  const [projectNameInput, setProjectNameInput] = useState("");

  const [projectPathInput, setProjectPathInput] = useState("");
  const [projectPathSearch, setProjectPathSearch] = useState("");
  const [projectPathInputOpen, setProjectPathInputOpen] = useState(false);
  const [gitlabProjectPathInputId, setGitlabProjectPathInputId] = useState<
    number | null
  >(null);

  const [frameworkInput, setFrameworkInput] = useState<string>(
    ReactFramework.VITE
  );
  const [mainFilePathInput, setMainFilePathInput] = useState("");
  const [projectSubfolderPathInput, setProjectSubfolderPathInput] =
    useState("");
  const [srcFolderPath, setSrcFolderPath] = useState("");
  const [customInstallCommandInput, setCustomInstallCommandInput] =
    useState("");
  const [devServerPortInput, setDevServerPortInput] = useState("3000");
  const [customRunCommandInput, setCustomRunCommandInput] = useState("");
  const [customInstallExtraCommandInput, setCustomInstallExtraCommandInput] =
    useState("");
  const [nodeVersionInput, setNodeVersionInput] = useState("18");
  const [packageManagerInput, setPackageManagerInput] = useState("npm");
  const [exampleComponentPath, setExampleComponentPath] = useState("");
  const [autoFormatEnabled, setAutoformatEnabled] = useState(true);
  const [autoInstallOff, setAutoInstallOff] = useState(false);
  const [baseBranchInput, setBaseBranchInput] = useState("");

  const [requestedRemix, setRequestedRemix] = useState(false);
  const [requestedNext15, setRequestedNext15] = useState(false);

  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [isInitializing, setIsInitializing] = useState(!!props.editProjectId);

  const [loadingUserRepos, setLoadingUserRepos] = useState(true);
  const [userRepos, setUserRepos] = useState([]);

  const { session } = useSession();

  const userId = useUserId();

  const [envVariablesSet, setEnvVariablesSet] = useState([
    {
      key: "",
      value: "",
    },
  ]);

  const initialTab = useSelector((state: any) => state.projects.initialTab);

  const inputsAndSetters = [
    {
      label: "Project Name",
      value: projectNameInput,
      setter: setProjectNameInput,
      optional: false,
      editableOnEdit: true,
      infoTooltip: null,
    },
    {
      label: "Repo URL",
      value: projectPathInput,
      setter: setProjectPathInput,
      optional: false,
      editableOnEdit: false,
      infoTooltip: "E.g. user/repo",
      renderHint: () => {
        if (!props.editProjectId && props.provider === GitProvider.GITHUB) {
          return (
            <NavLink
              className="text-xs inline-block text-white font-bold hover:underline text-left"
              to="https://github.com/apps/tempo-labs-ai/installations/new"
              target="_blank"
            >
              Don't see the repository you're looking for? Click here to add
              Tempo.
            </NavLink>
          );
        }

        return null;
      },
      renderInput: () => {
        if (loadingUserRepos) {
          return (
            <Input
              disabled={true}
              value="Loading Repos..."
              className="h-11 border-none rounded-tl-none rounded-bl-none"
            />
          );
        }

        if (props.editProjectId || !userRepos?.length) {
          return (
            <Input
              value={projectPathInput}
              onChange={(e) => {
                setProjectPathInput(e.target.value);
              }}
              disabled={Boolean(props.editProjectId)}
              className="h-11 border-none rounded-tl-none rounded-bl-none"
            />
          );
        }

        return (
          <div className="flex flex-col space-y-0.5">
            <Popover
              open={projectPathInputOpen}
              onOpenChange={setProjectPathInputOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className={cn(
                    "w-full rounded-sm rounded-tl-none rounded-bl-none h-11 border-none justify-between px-3 font-normal",
                    projectPathInput && "text-md"
                  )}
                >
                  <span>
                    {projectPathInput
                      ? projectPathInput
                      : "Select a repository..."}
                  </span>
                  <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[500px] p-1.5 z-[3010] rounded-xl">
                <Command>
                  <CommandInput
                    placeholder="Search respository..."
                    value={projectPathSearch}
                    onValueChange={setProjectPathSearch}
                    className="text-xs"
                  />
                  <CommandList>
                    <CommandGroup className="overflow-auto">
                      {userRepos
                        .filter((repo) => {
                          // Check if any repo name matches projectPathInput exactly
                          const isExactMatch = userRepos.some(
                            // @ts-expect-error TS2339
                            (r) => r.name === projectPathSearch
                          );

                          // If there's an exact match, return true for all repos (i.e., show all of them)
                          if (isExactMatch) return true;

                          if (!projectPathSearch) return true;

                          // Otherwise, filter based on whether the repo name starts with the input
                          // @ts-expect-error TS2339
                          return repo.name
                            ?.toLowerCase()
                            .includes(projectPathSearch.toLowerCase());
                        })
                        .map((repo) => (
                          <CommandItem
                            className="text-xs"
                            // @ts-expect-error TS2339
                            key={repo.name}
                            // @ts-expect-error TS2339
                            value={repo.name}
                            onSelect={(currentValue) => {
                              if (props.provider === GitProvider.GITLAB) {
                                // @ts-expect-error TS2339
                                setGitlabProjectPathInputId(repo.id);
                              }
                              setProjectPathInput(currentValue);
                              setProjectPathInputOpen(false);
                            }}
                          >
                            {/*
                             // @ts-expect-error TS2339 */}
                            {repo.name}
                            <CheckIcon
                              className={cn(
                                "ml-auto h-4 w-4",
                                // @ts-expect-error TS2339
                                projectPathInput === repo.name
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                          </CommandItem>
                        ))}
                      {projectPathSearch && (
                        <CommandItem
                          className="text-xs"
                          value={projectPathSearch}
                          onSelect={(currentValue) => {
                            setProjectPathInput(currentValue);
                            setProjectPathInputOpen(false);
                          }}
                        >
                          {projectPathSearch}
                          <CheckIcon
                            className={cn(
                              "ml-auto h-4 w-4",
                              projectPathInput === projectPathSearch
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                        </CommandItem>
                      )}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        );
      },
    },
    {
      label: "Framework",
      value: frameworkInput,
      setter: setFrameworkInput,
      optional: false,
      editableOnEdit: true,
      infoTooltip: "Select your framework",
      renderInput: () => (
        <Select
          onValueChange={(val) => {
            if (val === ReactFramework.REMIX) {
              return;
            }

            setFrameworkInput(val);
          }}
          value={frameworkInput || undefined}
        >
          <SelectTrigger className="w-full h-11 border-none rounded-sm rounded-tl-none rounded-bl-none">
            <SelectValue placeholder="Select Framework" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ReactFramework.NEXT_JS}>
              Next JS 13 & 14 (Pages Router)
            </SelectItem>
            <SelectItem value={ReactFramework.NEXT_JS_APP_ROUTER}>
              Next JS 13 & 14 (App Router)
            </SelectItem>
            <SelectItem value={ReactFramework.VITE}>Vite</SelectItem>
            <SelectItem value={ReactFramework.VITE_REACT_ROUTER_V7}>
              Vite React Router 7
            </SelectItem>
            <SelectItem value={ReactFramework.VITE_WITH_VIKE}>
              Vite with Vike
            </SelectItem>
            <SelectItem value={ReactFramework.EXPO}>Expo</SelectItem>
            <SelectItem disabled={true} value={""} className="!opacity-100">
              <div className="flex flex-row items-center justify-between gap-2">
                <span className="opacity-50">Remix (Coming Soon)</span>
                <Button
                  variant="primary"
                  size="xs"
                  className="pointer-events-auto !opacity-100 gap-1"
                  onClick={() => {
                    setRequestedRemix(true);
                    track("REQUEST_REMIX");
                  }}
                  disabled={requestedRemix}
                >
                  {requestedRemix && <CheckIcon className="w-3 h-3" />}
                  <span>{requestedRemix ? "Requested" : "Request"}</span>
                </Button>
              </div>
            </SelectItem>
            <SelectItem disabled={true} value={""} className="!opacity-100">
              <div className="flex flex-row items-center justify-between gap-2">
                <span className="opacity-50">NextJS 15 (Coming Soon)</span>
                <Button
                  variant="primary"
                  size="xs"
                  className="pointer-events-auto !opacity-100 gap-1"
                  onClick={() => {
                    setRequestedNext15(true);
                    track("REQUEST_NEXT_15");
                  }}
                  disabled={requestedNext15}
                >
                  {requestedNext15 && <CheckIcon className="w-3 h-3" />}
                  <span>{requestedNext15 ? "Requested" : "Request"}</span>
                </Button>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      ),
    },
    {
      label: "Node Version ",
      value: nodeVersionInput,
      setter: setNodeVersionInput,
      optional: false,
      editableOnEdit: true,
      infoTooltip: "18 by default -> translates to node:18-alpine",
      renderInput: () => (
        <Select
          onValueChange={(val) => {
            setNodeVersionInput(val);
          }}
          value={nodeVersionInput || undefined}
        >
          <SelectTrigger
            className={
              "rounded-sm rounded-tl-none rounded-bl-none border-none h-11"
            }
          >
            <SelectValue placeholder="Select Node Version" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={"20"}>20</SelectItem>
            <SelectItem value={"18"}>18</SelectItem>
            <SelectItem value={"16"}>16</SelectItem>
            <SelectItem value={"14"}>14</SelectItem>
            <SelectItem value={"12"}>12</SelectItem>
          </SelectContent>
        </Select>
      ),
    },
    {
      label: "Package Manager",
      value: packageManagerInput,
      setter: setPackageManagerInput,
      optional: false,
      editableOnEdit: true,
      renderInput: () => (
        <Select
          onValueChange={(val) => {
            setPackageManagerInput(val);
          }}
          value={packageManagerInput || undefined}
        >
          <SelectTrigger
            className={
              "rounded-sm rounded-tl-none rounded-bl-none border-none h-11"
            }
          >
            <SelectValue placeholder="Select Package Manager" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={"npm"}>npm</SelectItem>
            <SelectItem value={"yarn"}>yarn</SelectItem>
            <SelectItem value={"bun"}>bun</SelectItem>
            <SelectItem value={"pnpm"}>pnpm</SelectItem>
          </SelectContent>
        </Select>
      ),
    },
    {
      label: "Project Subfolder Path",
      value: projectSubfolderPathInput,
      setter: setProjectSubfolderPathInput,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "e.g. projects/client",
    },
    {
      label: "Main File Path",
      value: mainFilePathInput,
      setter: setMainFilePathInput,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "Relative to project top-level, e.g. client/pages/_app.tsx",
    },
    {
      label: "Base Git Branch",
      value: baseBranchInput,
      setter: setBaseBranchInput,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "If different from the default branch, e.g. (dev)",
    },
    {
      label: "Src Folder Path",
      value: srcFolderPath,
      setter: setSrcFolderPath,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "Relative to project top-level, e.g. client/src",
    },
    {
      label: "Dev Server Port",
      value: devServerPortInput,
      setter: setDevServerPortInput,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "e.g. if not 3000 (default)",
    },
    {
      label: "Custom Install Command",
      value: customInstallCommandInput,
      setter: setCustomInstallCommandInput,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "e.g. npm run install-all-deps",
    },
    {
      label: "Custom Run Command",
      value: customRunCommandInput,
      setter: setCustomRunCommandInput,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "e.g. npm run client-start",
    },
    {
      label: "Custom Install Libraries Command",
      value: customInstallExtraCommandInput,
      setter: setCustomInstallExtraCommandInput,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "e.g. npm run add-and-log",
    },
    {
      label: "Example Component Path",
      value: exampleComponentPath,
      setter: setExampleComponentPath,
      optional: true,
      editableOnEdit: true,
      infoTooltip:
        "Relative to project top-level, e.g. src/components/Header.tsx",
    },
    {
      label: "Autoformatter Enabled",
      value: autoFormatEnabled,
      setter: setAutoformatEnabled,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "Automatically formats all changes in tempo",
      renderInput: () => {
        return (
          <Switch
            className="ml-auto block mr-4"
            checked={autoFormatEnabled}
            onCheckedChange={setAutoformatEnabled}
          />
        );
      },
    },
    {
      label: "Turn off Auto-Install Dev Tools",
      value: autoInstallOff,
      setter: setAutoInstallOff,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "Does not auto-install the dev tools package",
      renderInput: () => {
        return (
          <Switch
            className="ml-auto block mr-4"
            checked={autoInstallOff}
            onCheckedChange={setAutoInstallOff}
          />
        );
      },
    },
  ];

  useEffect(() => {
    if (!session) {
      return;
    }

    const fetchUserRepos = async () => {
      let currentPage = 1;
      const perPage = 100; // Maximum allowed by GitHub API
      let hasMore = true;
      const allRepos = [];

      const supabaseJWT = await getUnexpiredToken(session);

      let url = `${
        import.meta.env.VITE_BACKEND_BASE_URL
      }/gitProvider/user/repos`;
      if (props.provider === GitProvider.GITLAB) {
        url += "?gitProvider=gitlab";
      }

      try {
        while (hasMore) {
          const response = await axios.get(url, {
            params: {
              page: currentPage,
              per_page: perPage,
            },
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          });

          if (response.data.length > 0) {
            // if gitlab, then the response is different:
            let mappedRepos = [];
            if (props.provider === GitProvider.GITLAB) {
              mappedRepos = response.data.map((repo) => {
                return { name: repo.path_with_namespace, id: repo.id };
              });
            } else {
              mappedRepos = response.data.map((repo) => {
                return { name: repo.full_name };
              });
            }

            allRepos.push(...mappedRepos);
            currentPage++;
          } else {
            hasMore = false;
          }
        }

        setUserRepos(allRepos);
      } catch (err) {
        logAxiosError("Error fetching repos", err);
      } finally {
        setLoadingUserRepos(false);
      }
    };

    if (!props.editProjectId) {
      setLoadingUserRepos(true);
      fetchUserRepos();
    } else {
      setLoadingUserRepos(false);
    }
  }, [Boolean(session)]);

  useEffect(() => {
    if (props.editProjectData) {
      getUnexpiredToken(session).then((supabaseJWT) => {
        axios
          .get(
            `${import.meta.env.VITE_BACKEND_BASE_URL}/projects/${
              props.editProjectId
            }/envVariables`,
            {
              headers: {
                Authorization: `Bearer ${supabaseJWT}`,
              },
            }
          )
          .then((res) => {
            const newEnvVariables = [];
            Object.keys(res.data).forEach((key) => {
              // @ts-expect-error TS2345
              newEnvVariables.push({
                key,
                value: SECRET_ENV_VARIABLE_VALUE,
              });
            });
            // @ts-expect-error TS2345
            newEnvVariables.push({
              key: "",
              value: "",
            });

            setEnvVariablesSet(newEnvVariables);
          })
          .catch((err) => {
            toast.error("Failed to fetch env variables for project");
          })
          .finally(() => {
            setIsInitializing(false);
          });
      });

      setProjectNameInput(props.editProjectData.name);
      setProjectPathInput(props.editProjectData.repoPath);
      setFrameworkInput(props.editProjectData.framework);
      setMainFilePathInput(props.editProjectData.mainFilePath);
      setProjectSubfolderPathInput(props.editProjectData.projectSubfolderPath);
      setSrcFolderPath(props.editProjectData.srcFolderPath);
      setCustomInstallCommandInput(props.editProjectData.customInstallCommand);
      setDevServerPortInput(props.editProjectData.devServerPort || "3000");
      setCustomRunCommandInput(props.editProjectData.customRunCommand);
      setCustomInstallExtraCommandInput(
        props.editProjectData.customInstallExtraCommand
      );
      setNodeVersionInput(props.editProjectData.nodeVersion || "18");
      setPackageManagerInput(props.editProjectData.packageManager || "npm");
      setExampleComponentPath(props.editProjectData.exampleComponentPath);
      setAutoformatEnabled(props.editProjectData.disableAutoformat !== true);
      setAutoInstallOff(props.editProjectData.auto_install_off);
      setBaseBranchInput(props.editProjectData.base_branch || "");
    }
  }, [props.editProjectData, props.editProjectId, Boolean(session)]);

  const createProject = () => {
    if (!projectNameInput) {
      toast.error("Missing project name input");
      return;
    }

    if (!projectPathInput) {
      toast.error("Missing project path input");
      return;
    }

    if (!frameworkInput) {
      toast.error("Missing framework input");
      return;
    }

    if (!nodeVersionInput) {
      toast.error("Missing node version input");
      return;
    }

    if (!packageManagerInput) {
      toast.error("Missing package manager input");
      return;
    }

    let urlToHit = `${import.meta.env.VITE_BACKEND_BASE_URL}/projects/add`;
    if (isAdd) {
      track("ADD_PROJECT", { type: "CONNECT_REPO" });
    } else {
      urlToHit = `${import.meta.env.VITE_BACKEND_BASE_URL}/projects/${
        props.editProjectId
      }/edit`;
      track("EDIT_PROJECT", {
        projectId: props.editProjectId,
      });
    }

    setIsCreatingProject(true);
    getUnexpiredToken(session).then((supabaseJWT) => {
      axios
        .post(
          urlToHit,
          {
            repoName: projectNameInput,
            repoPath: projectPathInput,
            framework: frameworkInput,
            mainFilePath: mainFilePathInput,
            srcFolderPath: srcFolderPath,
            customInstallCommand: customInstallCommandInput,
            devServerPort: devServerPortInput,
            customRunCommand: customRunCommandInput,
            customInstallExtraCommand: customInstallExtraCommandInput,
            nodeVersion: nodeVersionInput,
            packageManager: packageManagerInput,
            userId: userId,
            exampleComponentPath,
            projectSubfolderPath: projectSubfolderPathInput,
            disableAutoformat: !autoFormatEnabled,
            runMode: ProjectRunMode.CLOUD,
            base_branch: baseBranchInput,
            auto_install_off: autoInstallOff,
            provider: props.provider,
            gitlab_project_id:
              props.provider === GitProvider.GITLAB
                ? gitlabProjectPathInputId
                : null,
          },
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          }
        )
        .then((res) => {
          const environmentVariables = {};
          envVariablesSet.forEach((envVariable) => {
            if (!envVariable?.value) {
              return;
            }

            if (envVariable?.value === SECRET_ENV_VARIABLE_VALUE) {
              return;
            }

            if (envVariable?.value === DELETE_ENV_VARIABLE_CONSTANT) {
              if (!isAdd) {
                environmentVariables[envVariable.key] = null;
              }

              return;
            }

            environmentVariables[envVariable.key] = envVariable.value;
          });

          const createdProject = res.data.project;
          const createCanvas = res.data.canvas;

          if (isAdd) {
            if (res.data?.project?.templateUserId) {
              track("TRACK_USED_RUNNING_TEMPLATE", {
                used: true,
                repoPath: projectPathInput,
              });
            } else {
              track("TRACK_USED_RUNNING_TEMPLATE", {
                used: false,
                repoPath: projectPathInput,
              });
            }
          }

          if (!Object.keys(environmentVariables).length) {
            toast.success(!isAdd ? "Edited project" : "Created project");
            props.close(createdProject, createCanvas);
            return;
          }

          axios
            .post(
              `${import.meta.env.VITE_BACKEND_BASE_URL}/projects/${
                res.data?.project?.id
              }/envVariables`,
              environmentVariables,
              {
                headers: {
                  Authorization: `Bearer ${supabaseJWT}`,
                },
              }
            )
            .then((res) => {
              toast.success(!isAdd ? "Edited project" : "Created project");
              props.close(createdProject, createCanvas);
              return;
            })
            .catch((err) => {
              logAxiosError(
                `Error setting environment variables for ${
                  isAdd ? "added" : "edited"
                } project`,
                err
              );
              toast.error(
                `${
                  isAdd ? "Created" : "Edited"
                } project but could not set environment variables. Error: ${getAxiosErorrMessage(
                  err
                )}`
              );
            });
        })
        .catch((err) => {
          logAxiosError(`Error ${isAdd ? "adding" : "editing"} project`, err);
          toast.error(
            `Could not ${
              isAdd ? "add" : "edit"
            } project, check if path is valid. Error: ${getAxiosErorrMessage(
              err
            )}`
          );
        })
        .finally(() => {
          setIsCreatingProject(false);
        });
    });
  };

  const handleEnvVariables = (variables) => {
    setEnvVariablesSet(variables);
  };

  const isAdd = !props.editProjectId;

  return (
    <Dialog
      onOpenChange={(value) => {
        if (!value) {
          props.close();
        }
      }}
      open
      modal={false}
    >
      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        className="max-w-2xl border"
      >
        {(isCreatingProject || isInitializing) && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50 rounded-lg border">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">
                {isCreatingProject
                  ? (isAdd ? "Creating" : "Saving") + " project..."
                  : "Loading project data..."}
              </span>
            </div>
          </div>
        )}
        <DialogHeader>
          <DialogTitle>{isAdd ? "Add Project" : "Edit Project"}</DialogTitle>
          <DialogDescription>
            {isAdd
              ? `Import your ${
                  props.provider === GitProvider.GITLAB ? "GitLab" : "Github"
                } Project below`
              : "Edit your project below"}
          </DialogDescription>
        </DialogHeader>
        <Tabs variant="underline" defaultValue={initialTab} className="w-full">
          <TabsList className="w-full mb-4">
            <TabsTrigger value={"settings"}>Settings</TabsTrigger>
            <TabsTrigger value={"optional"}>Optional</TabsTrigger>
            <TabsTrigger value={"env-variables"}>Env Variables</TabsTrigger>
          </TabsList>
          <ScrollArea className="-mx-[1px] -mr-3 h-[280px] max-h-[calc(100vh-270px)]">
            <div className="p-[1px] pr-3">
              <TabsContent
                className="mt-0 flex flex-col gap-3 w-full"
                value="settings"
              >
                {inputsAndSetters.map((inputAndSetter) => {
                  if (inputAndSetter.optional) {
                    return;
                  }
                  return (
                    <React.Fragment key={inputAndSetter.label}>
                      <InputPrefixWrapper
                        label={inputAndSetter.label}
                        // @ts-expect-error TS2322
                        tooltipText={inputAndSetter.infoTooltip}
                      >
                        {inputAndSetter.renderInput ? (
                          inputAndSetter.renderInput()
                        ) : (
                          <Input
                            value={inputAndSetter.value?.toString?.()}
                            onChange={(e: any) => {
                              inputAndSetter.setter(e.target.value);
                            }}
                            disabled={!(isAdd || inputAndSetter.editableOnEdit)}
                            className="h-11 border-none rounded-tl-none rounded-bl-none grid-cols-2"
                          />
                        )}
                      </InputPrefixWrapper>
                      {inputAndSetter.renderHint
                        ? inputAndSetter.renderHint()
                        : null}
                    </React.Fragment>
                  );
                })}
              </TabsContent>
              <TabsContent
                className="mt-0 flex flex-col gap-3 w-full"
                value="optional"
              >
                {inputsAndSetters.map((inputAndSetter) => {
                  if (!inputAndSetter.optional) {
                    return;
                  }

                  return (
                    <InputPrefixWrapper
                      key={inputAndSetter.label}
                      label={inputAndSetter.label}
                      // @ts-expect-error TS2322
                      tooltipText={inputAndSetter.infoTooltip}
                    >
                      {inputAndSetter.renderInput ? (
                        inputAndSetter.renderInput()
                      ) : (
                        <Input
                          value={inputAndSetter.value?.toString?.()}
                          onChange={(e: any) => {
                            inputAndSetter.setter(e.target.value);
                          }}
                          className="h-11 border-none rounded-tl-none rounded-bl-none"
                        />
                      )}
                    </InputPrefixWrapper>
                  );
                })}
              </TabsContent>
              <TabsContent
                className="mt-0 flex flex-col gap-3 w-full"
                value="env-variables"
              >
                <EnvVariableHandler
                  handleEnvVariables={handleEnvVariables}
                  envVariablesSet={envVariablesSet}
                ></EnvVariableHandler>

                {envVariablesSet.map((envVariable, index) => {
                  if (envVariable.value === DELETE_ENV_VARIABLE_CONSTANT) {
                    return;
                  }

                  return (
                    <div className="flex items-center gap-2" key={index}>
                      <div className="w-full">
                        <Input
                          value={envVariable?.key}
                          onChange={(e) => {
                            setEnvVariablesSet((prev) => {
                              const copy = [...prev];
                              copy[index].key = e.currentTarget.value;
                              return copy;
                            });
                          }}
                          placeholder={"key"}
                          className="h-[46px]"
                        />
                      </div>
                      <div className="w-full">
                        <Input
                          className="h-[46px]"
                          value={
                            envVariable?.value === SECRET_ENV_VARIABLE_VALUE
                              ? "**************"
                              : envVariable?.value
                          }
                          onChange={(e) => {
                            setEnvVariablesSet((prev) => {
                              const copy = [...prev];
                              copy[index].value = e.currentTarget.value;
                              return copy;
                            });
                          }}
                          onFocus={() => {
                            if (
                              envVariable.value === SECRET_ENV_VARIABLE_VALUE
                            ) {
                              setEnvVariablesSet((prev) => {
                                const copy = [...prev];
                                copy[index].value = "";
                                return copy;
                              });
                            }
                          }}
                          placeholder="value"
                          aria-label="env-variable-value"
                        />
                      </div>
                      <Button
                        onClick={() => {
                          setEnvVariablesSet((prev) => {
                            const copy = [...prev];
                            copy[index].value = DELETE_ENV_VARIABLE_CONSTANT;
                            return copy;
                          });
                        }}
                        variant="outline"
                        className="text-muted-foreground h-[46px] py-3 hover:text-destructive"
                      >
                        <Trash2Icon className="w-5 h-5" />
                      </Button>
                    </div>
                  );
                })}
                <Button
                  variant="outline"
                  onClick={() => {
                    setEnvVariablesSet((prev) => {
                      const copy = [...prev];
                      copy.push({ key: "", value: "" });
                      return copy;
                    });
                  }}
                  size="lg"
                >
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Add Another
                </Button>
              </TabsContent>
            </div>
          </ScrollArea>
        </Tabs>
        <DialogFooter className="lg:gap-0 gap-2">
          <DialogClose asChild>
            <Button
              disabled={isCreatingProject}
              size="lg"
              className="w-full"
              variant="secondary"
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            size="lg"
            className="w-full"
            variant="primary"
            isLoading={isCreatingProject}
            disabled={isCreatingProject}
            onClick={createProject}
          >
            {isAdd ? "Create" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(AddEditProjectModal);
